import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { CHANNEL_SHORT_NAME_MAP, IChannel, PROTOCOL } from '@/entities/channel';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Link } from '@/shared/ui/lib/Link';
import { Stack, Typography } from '@/shared/ui';
import { ControlledTextField } from '@/shared/ui/TextField';

import { useAvitoConnect } from '../../model/avito/useAvitoConnect';
import { avitoConnectFormDataSchema } from '../../model/schemas';
import { ChannelIconWithLabel } from '../ChannelIconWithLabel';

const FORM_ID = 'avito-connect-form';
const AVITO_KEYS_URL = 'https://developers.avito.ru/applications';

interface AvitoConnectFormProps extends TDialogBase {
  channel: Pick<IChannel, 'id' | 'name'>;
  onAuthorize?: () => void;
}

export const AvitoConnectForm = ({
  open,
  onClose,
  onAuthorize,
  channel,
}: AvitoConnectFormProps) => {
  const { t } = useTranslation();
  const { connect, isPending } = useAvitoConnect();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    resolver: zodResolver(avitoConnectFormDataSchema(t)),
    defaultValues: {
      clientId: '',
      clientSecret: '',
      name: channel.name ?? '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = handleSubmit(async (data) => {
    await connect({
      channelId: channel.id,
      clientId: data.clientId,
      clientSecret: data.clientSecret,
      name: data.name,
    });
    reset();
    onAuthorize?.();
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('channels:avito.authTitle')}
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.AVITO]}
          protocol={PROTOCOL.AVITO}
        />
      }
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('channels:buttons.closeModal')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isPending}
        >
          {t('channels:avito.connect')}
        </Button>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit}>
        <Stack spacing={2}>
          <ControlledTextField
            name="clientId"
            control={control}
            autoFocus
            label={t('channels:avito.clientId')}
          />
          <ControlledTextField
            name="clientSecret"
            control={control}
            type="password"
            label={t('channels:avito.clientSecret')}
          />
          <Typography variant="body2">
            <Link href={AVITO_KEYS_URL} target="_blank" rel="noopener noreferrer">
              {t('channels:avito.keysLink')}
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('channels:avito.deliveryNote')}
          </Typography>
          <ControlledTextField
            name="name"
            control={control}
            label={t('channels:avito.name')}
          />
        </Stack>
      </form>
    </Dialog>
  );
};
